import { Suspense, type ReactNode } from 'react'
import { usePermissions } from './PermissionsContext'
import { ModuleErrorBoundary } from './ErrorBoundary'
import { Spinner } from './Spinner'

/**
 * Gate a module on permission, then contain its failures and its loading.
 *
 * Order matters: permission is checked before the module is even suspended, so a user without
 * access never triggers the network fetch for a remote they may not see.
 */
export function ModuleGate({
  moduleName,
  permissionKey,
  children,
}: {
  moduleName: string
  permissionKey: string
  children: ReactNode
}) {
  const { hasPermission, isLoading } = usePermissions()

  if (isLoading) return <Spinner />

  if (!hasPermission(permissionKey)) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-sm text-fg-secondary">
        <p className="text-lg font-medium text-fg">Access denied</p>
        <p className="text-sm text-fg-muted">
          You do not have permission to access this module.
        </p>
      </div>
    )
  }

  return (
    <ModuleErrorBoundary moduleName={moduleName}>
      <Suspense fallback={<Spinner />}>{children}</Suspense>
    </ModuleErrorBoundary>
  )
}
